/*
 * buy-buttons-pricing.js
 *
 * Keeps two bits of the product buy-buttons in sync with the selected
 * variant and quantity:
 *
 *   1) The live price inside the Add to Cart button (variant price x qty,
 *      honoring quantity price breaks when present).
 *   2) The "Covers N sq ft" readout next to the quantity selector
 *      (per-unit square footage x qty), shown only when the product has a
 *      custom.square_footage value.
 *
 * Per-variant data is rendered server-side as a JSON <script> tag with
 * [data-buy-buttons-data]. Each controller is scoped to one .product-form,
 * so multiple product forms on a page (e.g. quick view) stay independent.
 *
 * Variant changes come through the theme's FoxTheme.pubsub variantChange
 * event. Quantity changes come from the native quantity input's change/input
 * events. The price span is a sibling of .btn__text on purpose: the theme's
 * toggleAddButton() rewrites .btn__text innerHTML on variant change, which
 * would otherwise destroy the price.
 */
(function () {
  function moneyFmt(cents) {
    var formatted;
    if (window.FoxTheme && FoxTheme.Currency && FoxTheme.Currency.formatMoney) {
      var fmt = FoxTheme.settings && FoxTheme.settings.moneyFormat;
      formatted = FoxTheme.Currency.formatMoney(cents, fmt);
    } else {
      formatted = '$' + (cents / 100).toFixed(2);
    }
    // Mirror Liquid's money_without_trailing_zeros for the ATC button price:
    // drop the decimals when the amount is a whole currency unit (e.g.
    // $50.00 -> $50), keep them when there are real cents ($50.25 stays).
    // This matches the first-paint price rendered in buy-buttons.liquid so the
    // button doesn't flip from "$50" to "$50.00" on variant/quantity change.
    if (cents % 100 === 0) {
      formatted = formatted.replace(/([.,])0{2}(?=\D|$)/, '');
    }
    return formatted;
  }

  // Resolve the per-unit price for a quantity, honoring volume price breaks.
  function unitPriceForQty(rec, qty) {
    var price = rec.price;
    if (rec.breaks && rec.breaks.length) {
      var chosenMin = -1;
      rec.breaks.forEach(function (b) {
        if (qty >= b.min && b.min > chosenMin) {
          chosenMin = b.min;
          price = b.price;
        }
      });
    }
    return price;
  }

  function fmtCoverage(n) {
    var rounded = Math.round(n * 100) / 100;
    try {
      return rounded.toLocaleString(undefined, { maximumFractionDigits: 2 });
    } catch (e) {
      return String(rounded);
    }
  }

  function Controller(dataEl) {
    if (dataEl.dataset.bbBound) return;
    dataEl.dataset.bbBound = '1';

    this.form = dataEl.closest('.product-form');
    if (!this.form) return;

    this.sectionId = dataEl.getAttribute('data-buy-buttons-data');
    this.currentId = String(dataEl.getAttribute('data-current-variant') || '');

    try {
      this.data = JSON.parse(dataEl.textContent);
    } catch (e) {
      this.data = {};
    }

    this.priceTargets = this.form.querySelectorAll('[data-button-price]');
    this.coverage = this.form.querySelector('[data-coverage-for="' + this.sectionId + '"]');
    this.coverageValue = this.coverage ? this.coverage.querySelector('[data-coverage-value]') : null;
    this.qtyInput = this.form.querySelector('.quantity__input[name="quantity"]');

    this.bind();
    this.update();
  }

  Controller.prototype.getQty = function () {
    var v = this.qtyInput ? parseInt(this.qtyInput.value, 10) : 1;
    if (isNaN(v) || v < 1) v = 1;
    return v;
  };

  Controller.prototype.bind = function () {
    var self = this;

    if (this.qtyInput) {
      this.qtyInput.addEventListener('change', function () { self.update(); });
      this.qtyInput.addEventListener('input', function () { self.update(); });
    }

    if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) {
      FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, function (event) {
        var d = event && event.data;
        if (!d || String(d.sectionId) !== String(self.sectionId)) return;

        if (d.variant && d.variant.id != null) {
          self.currentId = String(d.variant.id);
          if (!self.data[self.currentId]) {
            // Variant not in the server-rendered map (rare). Fall back to the
            // event payload for price/availability; no sq ft or breaks data.
            self.data[self.currentId] = {
              price: d.variant.price,
              available: d.variant.available,
              sqft: null,
              breaks: []
            };
          } else {
            self.data[self.currentId].available = d.variant.available;
          }
        } else {
          // No variant selected (e.g. unavailable combination).
          self.currentId = '';
        }
        self.update();
      });
    }
  };

  Controller.prototype.update = function () {
    var rec = this.data[this.currentId];
    var qty = this.getQty();
    var available = rec ? rec.available !== false : false;
    var currentId = this.currentId;

    this.priceTargets.forEach(function (el) {
      if (!rec || typeof rec.price !== 'number' || !available) {
        el.setAttribute('hidden', '');
        el.textContent = '';
        return;
      }
      var total = unitPriceForQty(rec, qty) * qty;
      // Optional bulk-discount override from multiscount-tiered.js. 
      // No-ops when that script is absent or there's no discount & to preserve theme's button pricing.
      try {
        if (window.MultiscountTiered && typeof window.MultiscountTiered.lineTotalCents === 'function') {
          var override = window.MultiscountTiered.lineTotalCents(currentId, qty);
          if (typeof override === 'number' && isFinite(override)) total = override;
        }
      } catch (e) { /* fall back to the theme's total */ }
      el.textContent = moneyFmt(total);
      el.removeAttribute('hidden');
    });

    if (this.coverage && this.coverageValue) {
      var sqft = rec ? rec.sqft : null;
      if (sqft == null || sqft === '' || isNaN(Number(sqft))) {
        this.coverage.setAttribute('hidden', '');
      } else {
        this.coverageValue.textContent = fmtCoverage(Number(sqft) * qty);
        this.coverage.removeAttribute('hidden');
      }
    }
  };

  function init(root) {
    (root || document).querySelectorAll('script[data-buy-buttons-data]').forEach(function (el) {
      new Controller(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Re-scan when a section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
